import React, { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { userContext } from "../../../stores/users/userContext";
import { Box } from "../../design-system";
import { colors, gradients } from "../../design-system/colors";
import Sidebar from "../../pages/homepage/components/Sidebar";
import TopBar from "../../pages/homepage/components/TopBar";
import TabNavigation from "./TabNavigation";
import MyLibraryPageHeader from "./components/MyLibraryPageHeader";
import { UploadsTab, ExportsTab, SearchTab, CalendarTab, MeetingsTab } from "./tabs";
import {
  ROUTE_MY_LIBRARY,
  ROUTE_MY_LIBRARY_UPLOADS,
  ROUTE_MY_LIBRARY_EXPORTS,
  ROUTE_MY_LIBRARY_SEARCH,
  ROUTE_MY_LIBRARY_CALENDAR,
  ROUTE_MY_LIBRARY_MEETINGS,
} from "./constants/myLibraryText";
import { lookForPage, useLibraryLook } from "./looks/libraryLook";
import LibraryShell from "./looks/LibraryShell";
import SaidSearch from "./looks/SaidSearch";
import ArtisticPage from "../../studio/ArtisticPage";
import { ROUTES } from "../../../constants";

const SIDEBAR_WIDTH = 260;
const TOP_BAR_HEIGHT = 70;
const MAIN_CONTENT_MARGIN_RIGHT = 24;
const MAIN_CONTENT_MARGIN_BOTTOM = 24;

/**
 * My Library Container Component
 *
 * Main container for My Library page with routing structure and tab navigation.
 * Handles authentication checks and route-based tab content rendering.
 */
const MyLibraryContainer: React.FC = (): JSX.Element => {
  const userStore = useContext(userContext);
  const navigate = useNavigate();
  const location = useLocation();
  // The artistic-edit page and a quick look link exist only in the new looks.
  const pickedLook = lookForPage(useLibraryLook().look, location.search);
  const look = pickedLook === "classic" && location.pathname === ROUTES.MY_LIBRARY_ARTISTIC ? "strips" : pickedLook;

  // Redirect base route to default tab
  useEffect(() => {
    if (
      location.pathname === ROUTE_MY_LIBRARY ||
      location.pathname === "/my-library"
    ) {
      navigate(ROUTE_MY_LIBRARY_UPLOADS, { replace: true });
    }
  }, [location.pathname, navigate]);

  // Authentication check
  useEffect(() => {
    if (!userStore.isUserAuth()) {
      navigate("/login/");
      return;
    }

    // Wait for user data to load before checking stripe status
    // Check if user object has actual data (not just empty object {})
    const user = userStore.getUser();
    const hasUserData = user && Object.keys(user).length > 0 && (user.email || user.uuid);
    
    // Only redirect if user data is fully loaded and stripe is not active
    if (hasUserData && !userStore.isUserStripeActive()) {
      navigate("/subscription/");
      return;
    }
  }, [userStore, navigate]);

  // Determine active tab from route
  const isUploadsTab = location.pathname === ROUTE_MY_LIBRARY_UPLOADS;
  const isExportsTab = location.pathname === ROUTE_MY_LIBRARY_EXPORTS;
  const isSearchTab = location.pathname === ROUTE_MY_LIBRARY_SEARCH;
  const isCalendarTab = location.pathname === ROUTE_MY_LIBRARY_CALENDAR;
  const isMeetingsTab = location.pathname === ROUTE_MY_LIBRARY_MEETINGS;
  const isArtisticTab = location.pathname === ROUTES.MY_LIBRARY_ARTISTIC;

  // Render tab content based on route
  const renderTabContent = (): JSX.Element => {
    if (isUploadsTab) return <UploadsTab />;
    if (isExportsTab) return <ExportsTab />;
    if (isSearchTab) return <SearchTab />;
    if (isCalendarTab) return <CalendarTab />;
    if (isMeetingsTab) return <MeetingsTab />;
    return <UploadsTab />;
  };

  // Gallery / Strips: the light frame. Uploads, Exports and Search-what-was-said are drawn for it;
  // Meetings and Calendar keep their own (dark) look inside it for now.
  if (look !== "classic") {
    const content = isArtisticTab ? <ArtisticPage /> : isSearchTab ? <SaidSearch /> : renderTabContent();
    return (
      <LibraryShell>
        {isUploadsTab || isExportsTab || isSearchTab || isArtisticTab || (!isCalendarTab && !isMeetingsTab)
          ? content
          : <div className="panel">{content}</div>}
      </LibraryShell>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: colors.background[900],
        fontFamily: "Outfit, sans-serif",
      }}
    >
      <Sidebar />

      <Box
        sx={{
          marginLeft: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
          marginTop: `${TOP_BAR_HEIGHT}px`,
          marginRight: { xs: 0, md: `${MAIN_CONTENT_MARGIN_RIGHT}px` },
          marginBottom: `${MAIN_CONTENT_MARGIN_BOTTOM}px`,
          width: { xs: "100%", md: `calc(100% - ${SIDEBAR_WIDTH}px - ${MAIN_CONTENT_MARGIN_RIGHT}px)` },
          minHeight: `calc(100vh - ${TOP_BAR_HEIGHT}px - ${MAIN_CONTENT_MARGIN_BOTTOM}px)`,
          display: "flex",
          flexDirection: "column",
          background: gradients.mainBackground,
          borderRadius: { xs: 0, md: "16px" },
          padding: { xs: "16px 12px", md: "48px 32px 32px" },
        }}
      >
        <MyLibraryPageHeader />

        {/* Tab navigation */}
        <TabNavigation />

        {/* Tab content */}
        <Box sx={{ marginTop: "20px" }}>{renderTabContent()}</Box>
      </Box>

      <TopBar rightOffset={MAIN_CONTENT_MARGIN_RIGHT} />
    </Box>
  );
};

export default observer(MyLibraryContainer);
